import { NetworkError, TimeoutError, getStatus } from '@/api/errors';
import type { BargainCreateStatus } from '@/types/buy';

// Ожидание создания сделки после запроса на неё: сделку оформляет процесс Camunda, и номер сделки появляется не сразу.
// Общее для покупателя (`/app/buy/offer`) и продавца (`/app/sell/counter`): одна реализация, чтобы правка опроса не
// попадала только на одну из страниц.

export const POLL_INTERVAL_MS = 800;
export const POLL_DEADLINE_MS = 60_000;

/** Процесс создания сделки остановлен на стороне сервера. */
export class DealSuspendedError extends Error {}

/** Номер сделки не появился за отведённое время: сделка может ещё оформиться. */
export class DealTimeoutError extends Error {}

/**
 * Статус процесса узнать нельзя (опрос отклонён: истекла сессия, нет доступа, процесс не найден), а запрос на сделку
 * уже принят. Обрабатывается как истёкшее ожидание: страница ведёт в «Мои сделки», а не предлагает отправить запрос
 * повторно, иначе по одному предложению создались бы две сделки.
 */
export class DealStatusUnknownError extends DealTimeoutError {}

export interface WaitOptions {
  /** Запрос статуса процесса. */
  readonly getStatus: (processId: string) => Promise<BargainCreateStatus>;
  /** Страница ещё открыта: после ухода с неё опрос прекращается. */
  readonly isActive: () => boolean;
  readonly intervalMs?: number;
  readonly deadlineMs?: number;
  readonly sleep?: (ms: number) => Promise<void>;
  readonly now?: () => number;
}

const defaultSleep = (ms: number): Promise<void> =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

/**
 * Сбой опроса временный: нет ответа (сеть, тайм-аут) или сервер ответил 5xx, 408, 429. Всё остальное — отказ 4xx,
 * логическая ошибка ответа или ошибка в коде — повтором не исправить, и опрос на ней не крутится до крайнего срока.
 */
function isTransient(error: unknown): boolean {
  if (error instanceof NetworkError || error instanceof TimeoutError) {
    return true;
  }
  const code = getStatus(error);
  return code != null && (code >= 500 || code === 408 || code === 429);
}

/**
 * Ждёт номер созданной сделки. Сделка к этому моменту уже оформляется, поэтому сбой одного опроса (сеть, тайм-аут,
 * ошибка сервера) не означает отказа: опрос повторяется до крайнего срока. Иначе страница показала бы ошибку и
 * позволила отправить запрос на сделку повторно, и по одному предложению создались бы две сделки.
 *
 * @throws DealSuspendedError процесс остановлен
 * @throws DealTimeoutError срок истёк или страницу закрыли; `DealStatusUnknownError` — опрос отклонён
 */
export async function waitForBargain(processId: string, options: WaitOptions): Promise<number> {
  const sleep = options.sleep ?? defaultSleep;
  const now = options.now ?? Date.now;
  const deadline = now() + (options.deadlineMs ?? POLL_DEADLINE_MS);
  while (now() < deadline && options.isActive()) {
    await sleep(options.intervalMs ?? POLL_INTERVAL_MS);
    let status: BargainCreateStatus;
    try {
      status = await options.getStatus(processId);
    } catch (error) {
      if (!isTransient(error)) {
        // Исходная ошибка сохраняется в `cause`: её видит обработчик ошибок и журнал GlitchTip.
        throw new DealStatusUnknownError('Bargain status unavailable', { cause: error });
      }
      continue;
    }
    if (status.created && status.bargainId != null) {
      return status.bargainId;
    }
    if (status.suspended) {
      throw new DealSuspendedError();
    }
  }
  throw new DealTimeoutError();
}
